import type { Dispatch, SetStateAction } from "react";
import * as authClient from "@/app/api/auth-client";
import type { Ad } from "@/app/components/ad-card";
import type { CartItem, CartItemTradeIn } from "@/app/components/cart-page";
import type { ShippingOption } from "@/app/components/checkout-shipping-page";
import { getAdImage } from "@/app/utils/ad-utils";

export type HubDisplay = { city: string; state: string };

/** State slice needed to apply GET /auth/me/cart to UI (same logic as App session restore). */
export type BuyerCartHydrationDeps = {
  setCart: Dispatch<SetStateAction<CartItem[]>>;
  setCartOrderId?: ((id: string | null) => void) | null;
  setShippingOptions: Dispatch<SetStateAction<ShippingOption[]>>;
  setSelectedShippingId: (id: string | null) => void;
  setHubDisplay: Dispatch<SetStateAction<HubDisplay | null>>;
};

/**
 * Loads buyer cart from BFF and resolves line items for display.
 * Used on session restore and after Stripe return so hydration does not race with putCart([]).
 */
export async function hydrateBuyerCartFromServer(deps: BuyerCartHydrationDeps): Promise<void> {
  const cartRes = await authClient.getCart();
  const items = cartRes?.items ?? [];
  if (deps.setCartOrderId) {
    deps.setCartOrderId(cartRes?.order_id?.trim() || null);
  }
  if (items.length === 0) {
    deps.setCart([]);
    deps.setShippingOptions([]);
    deps.setSelectedShippingId(null);
    deps.setHubDisplay(null);
    return;
  }
  const cartItems = await Promise.all(
    items.map(async (it) => {
      let ad: Ad;
      try {
        const api = await authClient.getAdById(it.ad_id);
        ad = authClient.apiAdToAd(api);
      } catch {
        ad = {
          id: it.ad_id,
          title: "Produto indisponível",
          description: "",
          price: 0,
          category: "",
          location: "",
        };
      }
      const base = {
        ad,
        quantity: it.quantity,
        ...(it.offer_id && { offerId: it.offer_id, isOfferItem: true }),
      };
      if (!it.offer_id) {
        return {
          ...base,
          ...(it.agreed_price_cents != null && { offerPrice: it.agreed_price_cents / 100 }),
        };
      }
      try {
        const offer = await authClient.getOfferById(it.offer_id);
        const offerPrice = Number(offer.amount_cents) / 100;
        let offerTradeInAds: CartItemTradeIn[] | undefined;
        if (offer.product_ad_ids && offer.product_ad_ids.length > 0) {
          const tradeAds = await Promise.all(
            offer.product_ad_ids.map((id) =>
              authClient.getAdById(id).then((a) => authClient.apiAdToAd(a)).catch(() => null)
            )
          );
          offerTradeInAds = tradeAds
            .filter((a): a is Ad => a != null)
            .map((a) => ({
              id: a.id,
              value: a.price ?? 0,
              title: a.title,
              image: getAdImage(a),
            }));
        }
        return {
          ...base,
          offerPrice,
          ...(offerTradeInAds && offerTradeInAds.length > 0 && { offerTradeInAds }),
        };
      } catch {
        return {
          ...base,
          ...(it.agreed_price_cents != null && { offerPrice: it.agreed_price_cents / 100 }),
        };
      }
    })
  );
  deps.setCart(cartItems);
  deps.setShippingOptions([]);
  deps.setSelectedShippingId(null);
  deps.setHubDisplay(null);
}
